import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/Layout';
import { manutenzioneApi, motoApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Wrench, Plus, Trash2, Loader2, Calendar, Pencil, FileText, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { FotoUploader } from '../components/FotoUploader';
import { ListSkeleton, ManutenzioneCardSkeleton } from '../components/Skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIPI_MANUTENZIONE = [
  'Tagliando',
  'Cambio Olio',
  'Gomme',
  'Freni',
  'Catena',
  'Filtro Aria',
  'Candele',
  'Batteria',
  'Altro'
];

export default function Manutenzione() {
  const { t } = useTranslation();
  const [manutenzioni, setManutenzioni] = useState([]);
  const [motoList, setMotoList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedMoto, setSelectedMoto] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    moto_id: '',
    tipo: '',
    descrizione: '',
    km: 0,
    costo: 0,
    data: '',
    prossimo_km: null,
    foto_ricevuta: '',
    note: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadManutenzioni();
  }, [selectedMoto]);

  const loadData = async () => {
    try {
      const [motoRes] = await Promise.all([
        motoApi.getAll()
      ]);
      setMotoList(motoRes.data);
      await loadManutenzioni();
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const loadManutenzioni = async () => {
    try {
      const response = await manutenzioneApi.getAll(selectedMoto !== 'all' ? selectedMoto : undefined);
      setManutenzioni(response.data);
    } catch (error) {
      console.error('Error loading manutenzioni:', error);
    }
  };

  const resetForm = () => {
    const today = new Date();
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.${today.getFullYear()}`;
    setFormData({
      moto_id: motoList[0]?.id || '',
      tipo: '',
      descrizione: '',
      km: 0,
      costo: 0,
      data: formattedDate,
      prossimo_km: null,
      note: ''
    });
    setEditingId(null);
  };

  const handleOpenDialog = (manutenzione = null) => {
    if (manutenzione) {
      setFormData({
        moto_id: manutenzione.moto_id,
        tipo: manutenzione.tipo,
        descrizione: manutenzione.descrizione,
        km: manutenzione.km,
        costo: manutenzione.costo,
        data: manutenzione.data,
        prossimo_km: manutenzione.prossimo_km,
        note: manutenzione.note || ''
      });
      setEditingId(manutenzione.id);
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.moto_id || !formData.tipo || !formData.descrizione || !formData.data) {
      toast.error(t('errors.fillRequired'));
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await manutenzioneApi.update(editingId, formData);
        toast.success(t('success.updated'));
      } else {
        await manutenzioneApi.create(formData);
        toast.success(t('maintenance.registered'));
      }
      setDialogOpen(false);
      resetForm();
      loadManutenzioni();
    } catch (error) {
      toast.error(t('errors.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('maintenance.deleteConfirm'))) return;
    
    try {
      await manutenzioneApi.delete(id);
      toast.success(t('success.deleted'));
      loadManutenzioni();
    } catch (error) {
      toast.error(t('errors.deleteError'));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'CHF'
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('it-IT').format(value || 0);
  };

  const getMotoName = (motoId) => {
    const moto = motoList.find(m => m.id === motoId);
    return moto ? `${moto.marca} ${moto.modello}` : '—';
  };

  if (loading) {
    return (
      <Layout>
        <div className="page-container" data-testid="manutenzione-page">
          <div className="page-header">
            <h1 className="page-title">Manutenzione</h1>
            <p className="page-subtitle">Registro interventi</p>
          </div>
          <ListSkeleton count={4} Component={ManutenzioneCardSkeleton} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container" data-testid="manutenzione-page">
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Manutenzione</h1>
            <p className="page-subtitle">Registro interventi</p>
          </div>
          
          <div className="flex gap-3">
            {motoList.length > 1 && (
              <Select value={selectedMoto} onValueChange={setSelectedMoto}>
                <SelectTrigger className="w-48 bg-black/50 border-[#262626] rounded-none h-12">
                  <SelectValue placeholder="Tutte le moto" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                  <SelectItem value="all">Tutte le moto</SelectItem>
                  {motoList.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.marca} {m.modello}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => handleOpenDialog()}
                  disabled={motoList.length === 0}
                  className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm h-12 px-6"
                  data-testid="add-manutenzione-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  {t('maintenance.register')}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0a0a0a] border-[#262626] max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-['Teko'] text-2xl text-white uppercase">
                    {editingId ? t('maintenance.editMaintenance') : t('maintenance.newMaintenance')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label className="form-label">Moto *</Label>
                    <Select 
                      value={formData.moto_id} 
                      onValueChange={(v) => setFormData({...formData, moto_id: v})}
                    >
                      <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11" data-testid="man-moto-select">
                        <SelectValue placeholder="Seleziona moto" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                        {motoList.map(m => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.marca} {m.modello}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">Tipo *</Label>
                      <Select 
                        value={formData.tipo} 
                        onValueChange={(v) => setFormData({...formData, tipo: v})}
                      >
                        <SelectTrigger className="bg-black/50 border-[#262626] rounded-none h-11" data-testid="man-tipo-select">
                          <SelectValue placeholder="Tipo intervento" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-[#262626]">
                          {TIPI_MANUTENZIONE.map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="form-label">Data *</Label>
                      <Input
                        value={formData.data}
                        onChange={(e) => setFormData({...formData, data: e.target.value})}
                        placeholder="GG.MM.AAAA"
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                        data-testid="man-data"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="form-label">Descrizione *</Label>
                    <Input
                      value={formData.descrizione}
                      onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                      placeholder="es. Cambio olio motore + filtro"
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11"
                      data-testid="man-descrizione"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">Km al momento</Label>
                      <Input
                        type="number"
                        value={formData.km}
                        onChange={(e) => setFormData({...formData, km: parseInt(e.target.value) || 0})}
                        onFocus={(e) => e.target.select()}
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                        data-testid="man-km"
                      />
                    </div>
                    <div>
                      <Label className="form-label">Costo (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.costo}
                        onChange={(e) => setFormData({...formData, costo: parseFloat(e.target.value) || 0})}
                        onFocus={(e) => e.target.select()}
                        className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                        data-testid="man-costo"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="form-label">Prossimo intervento a km</Label>
                    <Input
                      type="number"
                      value={formData.prossimo_km || ''}
                      onChange={(e) => setFormData({...formData, prossimo_km: e.target.value ? parseInt(e.target.value) : null})}
                      onFocus={(e) => e.target.select()}
                      placeholder="es. 15000"
                      className="bg-black/50 border-[#262626] focus:border-[#FF5F1F] rounded-none h-11 font-mono"
                      data-testid="man-prossimo-km"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      className="flex-1 h-11 border-[#262626] text-white hover:bg-white/5 uppercase tracking-wider rounded-sm"
                    >
                      Annulla
                    </Button>
                    <Button
                      type="submit"
                      disabled={saving}
                      className="flex-1 h-11 bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm"
                      data-testid="man-submit"
                    >
                      {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Registra'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {motoList.length === 0 ? (
          <div className="vault-card text-center py-16">
            <Wrench className="w-16 h-16 text-[#4B5563] mx-auto mb-4" />
            <h3 className="text-white text-xl mb-2">Prima aggiungi una moto</h3>
            <p className="text-[#9CA3AF]">Devi avere almeno una moto per registrare le manutenzioni</p>
          </div>
        ) : manutenzioni.length === 0 ? (
          <div className="vault-card text-center py-16">
            <Wrench className="w-16 h-16 text-[#4B5563] mx-auto mb-4" />
            <h3 className="text-white text-xl mb-2">Nessuna manutenzione registrata</h3>
            <p className="text-[#9CA3AF] mb-6">Inizia a tracciare gli interventi sulla tua moto</p>
            <Button 
              onClick={handleOpenDialog}
              className="bg-[#FF5F1F] hover:bg-[#FF5F1F]/90 text-white uppercase tracking-wider font-bold rounded-sm h-11 px-6"
            >
              <Plus className="w-5 h-5 mr-2" />
              Registra Manutenzione
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {manutenzioni.map((man) => (
              <div
                key={man.id}
                className="vault-card flex flex-col sm:flex-row sm:items-center gap-4"
                data-testid={`manutenzione-item-${man.id}`}
              >
                <div className="w-12 h-12 bg-[#FF5F1F]/10 flex items-center justify-center flex-shrink-0">
                  <Wrench className="w-6 h-6 text-[#FF5F1F]" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="vault-badge vault-badge-orange">{man.tipo}</span>
                    <span className="text-[#9CA3AF] text-sm">{getMotoName(man.moto_id)}</span>
                  </div>
                  <h3 className="text-white font-medium">{man.descrizione}</h3>
                  {man.prossimo_km && (
                    <p className="text-[#9CA3AF] text-sm mt-1">
                      Prossimo intervento: <span className="font-mono text-[#FF5F1F]">{formatNumber(man.prossimo_km)} km</span>
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-6 sm:gap-8">
                  <div className="text-right">
                    <p className="text-[#FF5F1F] font-mono text-lg">{formatNumber(man.km)} km</p>
                    <p className="text-[#9CA3AF] text-xs flex items-center gap-1 justify-end">
                      <Calendar className="w-3 h-3" />
                      {man.data}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-mono">{formatCurrency(man.costo)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenDialog(man)}
                      className="p-2 text-[#9CA3AF] hover:text-[#FF5F1F] transition-colors"
                      data-testid={`edit-man-${man.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(man.id)}
                      className="p-2 text-[#9CA3AF] hover:text-red-500 transition-colors"
                      data-testid={`delete-man-${man.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
